/**
 * Pure helpers for Tool Inspection summary card: previous shift, left over, final count, used.
 * Unit-tested; used by ToolInspectionPage.
 */

/**
 * Previous consecutive shift: Night -> same day Day; Day -> previous day Night.
 * @param {string} d - Date YYYY-MM-DD
 * @param {string} sh - 'Day' | 'Night'
 * @returns {{ date: string, shift: string } | null}
 */
export function getPreviousConsecutiveShift(d, sh) {
  if (!d || !sh) return null;
  if (sh === 'Night') return { date: d, shift: 'Day' };
  const prev = new Date(d + 'T12:00:00');
  prev.setDate(prev.getDate() - 1);
  const prevDate = prev.toISOString().slice(0, 10);
  return { date: prevDate, shift: 'Night' };
}

/**
 * Compute left over per insert type from a log's summary.insert_counts (issued - used).
 * @param {Array<{ insert_type_code?: string, quantity_issued?: number, quantity_used?: number }>} insertCounts
 * @returns {Record<string, number>}
 */
export function computeLeftOverFromInsertCounts(insertCounts) {
  if (!Array.isArray(insertCounts)) return {};
  const left = {};
  insertCounts.forEach((ic) => {
    if (ic.insert_type_code == null || ic.insert_type_code === '') return;
    const issued = Number(ic.quantity_issued) || 0;
    const used = Number(ic.quantity_used) || 0;
    left[ic.insert_type_code] = Math.max(0, issued - used);
  });
  return left;
}

/**
 * Final count for one insert type: left over + issued - used (min 0).
 * @param {number} leftOver
 * @param {number} issued
 * @param {number} used
 * @returns {number}
 */
export function computeFinalCount(leftOver, issued, used) {
  return Math.max(0, (leftOver || 0) + (issued || 0) - (used || 0));
}

/**
 * Total inserts used per insert type from machine data. Only counts tools that are filled (tool code + insert type set).
 * @param {Record<string, { tools?: Array<{ tool_code?: string, insert_type?: string, total_inserts_used?: number }> }>} machineData
 * @returns {Record<string, number>}
 */
export function computeUsedByInsertType(machineData) {
  const map = {};
  if (!machineData || typeof machineData !== 'object') return map;
  for (const data of Object.values(machineData)) {
    const tools = data?.tools || [];
    for (const tool of tools) {
      const code = tool.insert_type?.trim?.() || tool.insert_type;
      if (!code || !(tool.tool_code?.trim?.() || tool.tool_code)) continue;
      const total = tool.total_inserts_used ?? 0;
      map[code] = (map[code] || 0) + total;
    }
  }
  return map;
}
